<template>
  <BittsTooltip trigger="hover" v-bind="tooltipBinds">
    <BittsButton
      v-if="showAddToCrm"
      size="x-small"
      :text="buttonText"
      :aria-disabled="buttonDisabled"
      :left-icon="buttonIcon"
      :variant="variant"
      @click.prevent.stop="addToCRM"
    />
    <template #title>
      <p style="text-align: center"> You have this contact in your CRM </p>
      <BittsButton
        type="white"
        size="x-small"
        text="View in CRM"
        :right-icon="['fak', 'external-link']"
        style="width: 100%"
        class="mt-10"
        @click.prevent="navigateToContact"
        @click.middle="navigateToContact"
        @mousedown.middle.prevent.stop
      />
    </template>
  </BittsTooltip>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsButtonVariant,
  BittsIcon,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';
import { Nullable } from '@crossbeam/types';

import axios from 'axios';
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useRouteViewTracker } from '@/components/sales/AccountDrawer/composables/useRouteViewTracker';

import useAuth from '@/composables/useAuth';
import { useDealNavActivities } from '@/composables/useDealNavActivities';
import useIteratively from '@/composables/useIteratively';
import { WRITE_ACCESS_ROLES } from '@/constants/team';
import { ProvidedHasSalesforceFeed } from '@/injectionKeys/account_drawer';
import { useCopilotContactsStore, useFlashesStore } from '@/stores';
import { PartnerContact } from '@/types/copilotContacts';
import urls from '@/urls';

const { contact, variant = 'outline' } = defineProps<{
  contact: PartnerContact['first_contact'];
  variant?: BittsButtonVariant;
}>();

const route = useRoute();
const routeView = useRouteViewTracker((route?.name || '') as string);

const contactsStore = useCopilotContactsStore();
const flashesStore = useFlashesStore();

const recordId = computed<string>(() => contactsStore.recordId ?? '');

const { currentAuth } = useAuth();
const { iteratively } = useIteratively();
const { trackDealNavActivity } = useDealNavActivities();

const crmId = ref(null);

const inOwnCrm = ref(contact.in_own_crm);
const saved = ref(contact.saved);
const savingToCRM = ref(false);

const showHover = computed(
  () => !!contactsStore.getCRMUrl && (!!contact.crm_id || !!crmId.value),
);

const tooltipBinds = computed(() => {
  if (!showHover.value) {
    return { showTooltip: showHover.value };
  }
  return {};
});

const hasSalesforceFeed = inject(ProvidedHasSalesforceFeed);

const showAddToCrm = computed(
  () =>
    !!contact.last_name &&
    WRITE_ACCESS_ROLES.includes(currentAuth.value?.sales_edge_role || '') &&
    contactsStore.getContactsSources.length &&
    hasSalesforceFeed?.value,
);

const buttonText = computed(() => {
  if (!inOwnCrm.value && !savingToCRM.value) {
    return 'Add';
  } else if (savingToCRM.value) {
    return 'Saving...';
  }
  return 'Added';
});

const buttonDisabled = computed(
  () => savingToCRM.value || saved.value || inOwnCrm.value,
);

const buttonIcon = computed(() => {
  if (inOwnCrm.value || saved.value) {
    return ['fak', 'check'] as BittsIcon;
  } else if (!inOwnCrm.value && !savingToCRM.value) {
    return ['fak', 'add'] as BittsIcon;
  }
  return undefined; // No icon when saving!
});

function navigateToContact(event: Event) {
  event.preventDefault();
  const targetCrmID = contact.crm_id || crmId.value;
  if (contactsStore.getCRMUrl && targetCrmID) {
    const targetUrl = `${contactsStore.getCRMUrl}/${targetCrmID}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
}
type AddToCrmPayload = {
  first_name: Nullable<string>;
  last_name: string;
  email: string;
  phone: Nullable<string>;
  title: Nullable<string>;
};
function trackAddToCRM(payload: AddToCrmPayload) {
  iteratively.dealNavDrawerAction({
    type: 'user clicks add to CRM',
    metadata: {
      payload,
      account: contactsStore.account,
    },
    overlap_company_domain: contactsStore.account?.domain,
    overlap_company_name: contactsStore.account?.name,
    event_site: EVENT_SITES.DEAL_NAV_DRAWER_CONTACT_DETAIL,
    view: routeView,
  } as never);
  trackDealNavActivity('user_added_contact_to_crm', recordId.value, {
    contact_name: `${payload.first_name} ${payload.last_name}`,
    contact_email: payload.email,
  });
}

async function addToCRM() {
  savingToCRM.value = true;
  const payload = {
    first_name: contact.first_name || null,
    last_name: contact.last_name,
    email: contact.partner_contact_email,
    phone: contact.partner_contact_phone || null,
    title: contact.partner_contact_title || null,
  };
  try {
    const response = await axios.post(
      urls.salesEdge.records.addToCrm(recordId.value),
      payload,
    );
    await contactsStore.refreshCopilotContactsStore();
    trackAddToCRM(payload as AddToCrmPayload);
    const { id: freshCRMId } = response.data;
    crmId.value = freshCRMId;
    inOwnCrm.value = true;
    saved.value = true;
  } catch (error) {
    const errorMessage = axios.isAxiosError(error)
      ? error.response?.data?.errors?.[0] || error.message
      : 'An unknown error occurred';
    flashesStore.addErrorFlash({
      message: 'Failed to save to CRM',
      description: errorMessage,
    });
  } finally {
    savingToCRM.value = false;
  }
}
</script>
